import { useEffect, useRef } from 'react'
import type { ZOrderDirection } from '../utils/canvasZOrder'

interface UseCanvasZOrderShortcutOptions {
  /** Nur im Maus-Werkzeug des sichtbaren Tabs (TabStack-Falle). */
  enabled: boolean
  /**
   * Das Canvas-Element, an dem gehorcht wird — NICHT das `window`.
   * `null`, solange React Flow seine Pane noch nicht aufgebaut hat.
   */
  container: HTMLElement | null
  /** Ganz nach vorn bzw. ganz nach hinten — für die Auswahl. Ändert es nichts, tut es nichts. */
  onZOrder: (direction: ZOrderDirection) => void
}

/**
 * **⇧⌘↑ / ⇧⌘↓** (Windows ⇧Strg) — die Auswahl ganz nach vorn bzw. ganz nach
 * hinten (CONTEXT.md, „Vorn und hinten").
 *
 * Pfeile statt `[`/`]` wie in Figma: Die Klammern liegen auf der deutschen
 * Tastatur auf ⌥5/⌥6. `code`, damit es auf jedem Layout dieselbe Taste ist.
 *
 * **Am Canvas-Element, nicht am `window`** — dieselbe Regel wie ⌫ und ⌘D
 * (`useCanvasDeleteShortcut`, `useCanvasDuplicateShortcut`): Es zählt, wo der
 * Fokus liegt. In der Aufgabenliste daneben kommt das Kürzel gar nicht an.
 *
 * **Die Taste wird IMMER aufgehalten**, auch wenn sich nichts ändert: React
 * Flow schiebt ein ausgewähltes Element mit den Pfeiltasten (⇧ vierfach), und
 * ein Zettel, der schon ganz vorn liegt, rückte sonst 20px nach oben. Der
 * native Listener hier läuft vor React Flows `onKeyDown`, das erst an der
 * Wurzel ankommt.
 *
 * Nicht in Textfeldern: Dort markiert ⇧⌘↑ bis zum Anfang.
 *
 * @example
 * useCanvasZOrderShortcut({ enabled, container: domNode, onZOrder })
 */
export function useCanvasZOrderShortcut({ enabled, container, onZOrder }: UseCanvasZOrderShortcutOptions) {
  // Über ein Ref, damit der Listener nicht bei jeder Änderung an der Auswahl neu gebunden wird.
  const onZOrderRef = useRef(onZOrder)
  onZOrderRef.current = onZOrder

  useEffect(() => {
    if (!enabled || !container) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'ArrowUp' && e.code !== 'ArrowDown') return
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return
      if (e.repeat) return
      // Einem offenen Menü seine Tasten lassen.
      if (document.querySelector('[role="menu"]')) return
      const target = e.target as HTMLElement | null
      // Der **Text** schreibt in ein `contenteditable` (ADR 0029), Zettel und Memo in ein Textfeld.
      // `closest` statt nur `isContentEditable`: jsdom kennt Letzteres nicht.
      const typing = target?.isContentEditable
        || Boolean(target?.closest('[contenteditable]:not([contenteditable="false"])'))
        || ((target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) && !target.readOnly)
      if (typing) return

      e.preventDefault()
      e.stopPropagation()
      onZOrderRef.current(e.code === 'ArrowUp' ? 'front' : 'back')
    }

    container.addEventListener('keydown', handleKeyDown)
    return () => container.removeEventListener('keydown', handleKeyDown)
  }, [enabled, container])
}
